import toast from "react-hot-toast";
import {InvalidAuthException} from "@app/utils/InvalidAuthException";
import {
  ActivationError,
  ActivationErrorBody,
  InvoiceResponse,
  PaymentMethodResponse,
  SetupIntentResponse,
  SubscriptionResponse,
  SubscriptionTier,
  TierResponse,
  TopUpCatalogResponse,
  TopUpCheckoutItem,
  TopUpCheckoutResponse,
  TrialOfferResponse,
  UpgradePreviewResponse
} from "@app/Subscription/types/subscription";

export function accessibleRouteChangeHandler() {
  return window.setTimeout(() => {
    const mainContainer = document.getElementById("primary-app-container");
    if (mainContainer) {
      mainContainer.focus();
    }
  }, 50);
}

export function getBearerToken() {
  const tokenString = localStorage.getItem('token');
  if (tokenString != null && tokenString != "undefined") {
    return tokenString;
  }
  return null
}

// --- Centralized auth-aware fetch wrapper ---

let _loggingOut = false;

/**
 * Wrapper around fetch that:
 * 1. Prepends the API base URL
 * 2. Injects the Authorization header
 * 3. Detects 401 responses and triggers a single logout
 * 4. Short-circuits all calls once a logout is in progress
 */
async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  if (_loggingOut) {
    return new Response(null, { status: 401, statusText: 'Logging out' });
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + getBearerToken(),
    ...(options.headers || {})
  };

  const response = await fetch(process.env["API_URL"] + path, { ...options, headers });

  if (response.status === 401 && !_loggingOut) {
    _loggingOut = true;
    logout();
    return response;
  }

  return response;
}

// --- Logout ---

export const logout = () => {
  _loggingOut = true;
  localStorage.clear();
  sessionStorage.clear();
  window.location.reload();
}

// --- Response parsing ---

function parseResponse(response: Response) {
  if (response.ok) {
    return response.json();
  }
  // 401 is already handled by apiFetch; return undefined for non-ok
  return undefined;
}

function parseAuthResponse(response: Response) {
  if (response.ok) {
    return response.json();
  } else if (response.status == 401) {
    throw new InvalidAuthException("Invalid credentials")
  }
  return undefined;
}

// --- GET requests ---

export async function getOrganizations() {
  return apiFetch('/profiles').then(parseResponse);
}

export async function getUserInfo() {
  return apiFetch('/users/me').then(parseResponse);
}

export async function getAds() {
  return apiFetch('/ads').then(parseResponse);
}

export async function getAdById(adId: string) {
  return apiFetch('/ads/' + adId).then(parseResponse);
}

export async function getAdsByOrganizationId(id) {
  return apiFetch('/ads?organizationId=' + id).then(parseResponse);
}

export async function getCampaigns() {
  return apiFetch('/campaigns').then(parseResponse);
}

export async function getCampaignById(campaignId: string) {
  return apiFetch('/campaigns/' + campaignId).then(parseResponse);
}

export async function getCampaignsByOrganizationId(id) {
  return apiFetch('/campaigns?organizationId=' + id).then(parseResponse);
}

// --- DELETE requests ---

export async function deleteAd(adId: string) {
  const response = await apiFetch('/ads/' + adId, { method: 'DELETE' });
  if (response.ok) {
    return { success: true };
  }
  throw new Error('Failed to delete ad');
}

export async function deleteOrganization(orgId: string) {
  const response = await apiFetch('/organizations/' + orgId, { method: 'DELETE' });
  if (response.ok) {
    return { success: true };
  }
  throw new Error('Failed to delete organization');
}

export async function deleteCampaign(campaignId: string) {
  const response = await apiFetch('/campaigns/' + campaignId, { method: 'DELETE' });
  if (response.ok) {
    return { success: true };
  }
  throw new Error('Failed to delete campaign');
}

// --- POST / PUT requests ---

export async function stopCampaign(campaignId: string) {
  const response = await apiFetch('/campaigns/' + campaignId + '/stop', { method: 'POST' });
  if (response.ok) {
    return { success: true };
  }
  throw new Error('Failed to stop campaign');
}

export async function createAd(payload) {
  const response = await apiFetch('/ads', {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    // 402 = AI ad gating (no_active_subscription / ai_not_in_tier / ai_limit)
    if (response.status === 402) {
      const body = await parseStructuredError(response);
      throw new ActivationError(402, body);
    }
    const errorText = await response.text();
    const error: any = new Error(errorText || 'Failed to create ad');
    error.status = response.status;
    try {
      error.data = JSON.parse(errorText);
    } catch (e) {
      error.data = null;
    }
    throw error;
  }
  toast("Successfully created Ad!");
  return response.json();
}

export async function createAccount(payload) {
  const response = await apiFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const errorText = await response.text();
    const error: any = new Error(errorText || 'Failed to create account');
    error.status = response.status;
    try {
      error.data = JSON.parse(errorText);
    } catch (e) {
      error.data = null;
    }
    throw error;
  }
  toast("Successfully created account!");
  return response;
}

export async function registerOrganization(payload) {
  const response = await apiFetch('/organizations', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  toast("Successfully registered Organization!");
  return parseResponse(response);
}

export async function updateOrganization(id, payload) {
  const response = await apiFetch('/organizations/' + id, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    toast.error("Failed to update Organization");
    throw new Error('Failed to update organization (' + response.status + ')');
  }
  toast("Successfully updated Organization!");
  return parseResponse(response);
}

export async function triggerBusinessResearch(orgId: string) {
  const response = await apiFetch('/organizations/' + orgId + '/research', { method: 'POST' });
  return parseResponse(response);
}

export async function toggleBusinessSummary(orgId: string, enabled: boolean) {
  const response = await apiFetch('/organizations/' + orgId + '/toggle-business-summary', {
    method: 'POST',
    body: JSON.stringify({ enabled })
  });
  return parseResponse(response);
}

export async function updateBusinessSummaryText(orgId: string, businessSummary: string) {
  const response = await apiFetch('/organizations/' + orgId + '/business-summary', {
    method: 'POST',
    body: JSON.stringify({ businessSummary })
  });
  return parseResponse(response);
}

export async function updateAd(id, payload) {
  const response = await apiFetch('/ads/' + id, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  toast("Successfully updated Ad!");
  return parseResponse(response);
}

export async function reviewAd(adId: string, approved: boolean, feedback?: string) {
  const body: any = { approved };
  if (!approved && feedback) {
    body.feedback = feedback;
  }

  const response = await apiFetch('/ads/' + adId + '/review', {
    method: 'POST',
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to review ad');
  }

  return response.json();
}

export type ReviewAction = 'APPROVE' | 'TWEAK' | 'REJECT_ALL';

export interface AdSampleDto {
  sampleIndex: number;
  s3Uri: string;
  presignedUrl?: string;
}

// Multi-sample review: pick a sample and approve (publish), tweak (refine that
// sample), or reject the whole batch with feedback (regenerate).
export async function reviewAdAction(
  adId: string,
  action: ReviewAction,
  opts?: { chosenSampleS3Uri?: string; feedback?: string }
) {
  const body: any = { action };
  if (opts?.chosenSampleS3Uri) body.chosenSampleS3Uri = opts.chosenSampleS3Uri;
  if (opts?.feedback) body.feedback = opts.feedback;

  const response = await apiFetch('/ads/' + adId + '/review', {
    method: 'POST',
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to review ad');
  }

  return response.json();
}

export async function getIterationHistory(adId: string) {
  return apiFetch('/ads/' + adId + '/iterations').then(parseResponse);
}

export async function submitAdPrompt(adId, prompt) {
  const response = await apiFetch('/ads/' + adId, {
    method: 'POST',
    body: JSON.stringify({ prompt: prompt })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to submit ad prompt');
  }

  return response.json();
}

export async function updateCampaign(id, payload) {
  const response = await apiFetch('/campaigns/' + id, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
  toast("Successfully updated Campaign!");
  return parseResponse(response);
}

export async function loginUser(credentials) {
  // Login does not use apiFetch — no auth header needed, and 401 means bad credentials, not expired token
  const response = await fetch(process.env["API_URL"] + '/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(credentials)
  });

  if (response.ok) {
    return response.json();
  } else if (response.status === 401) {
    throw new InvalidAuthException("Invalid credentials");
  } else if (response.status === 403) {
    const message = await response.text();
    const error: any = new Error(message || "Forbidden");
    error.status = 403;
    throw error;
  }
}

export async function resendConfirmationEmail(email: string) {
  const response = await fetch(process.env["API_URL"] + '/auth/resend-confirmation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  });
  return response;
}

export async function forgotPassword(email: string) {
  const response = await fetch(process.env["API_URL"] + '/auth/forgot-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  });
  return response;
}

export async function resetPassword(token: string, newPassword: string) {
  const response = await fetch(process.env["API_URL"] + '/auth/reset-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ token, newPassword })
  });
  return response;
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const response = await apiFetch('/users/me/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword })
  });
  return response;
}

export async function createCampaign(payload) {
  const response = await apiFetch('/campaigns', {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorText = await response.text();
    const error: any = new Error(errorText || 'Failed to create campaign');
    error.status = response.status;
    try {
      error.data = JSON.parse(errorText);
    } catch (e) {
      error.data = null;
    }
    throw error;
  }

  return response.json();
}

// --- Slot booking (per-run) ---

export interface SlotOption {
  slotStart: string;            // ISO 8601 UTC instant
  eligibleDriverCount: number;
}

export interface CampaignRun {
  id?: number;
  runId?: number;
  campaignId?: number;
  slotStart: string;            // ISO 8601 UTC
  slotEnd?: string;             // ISO 8601 UTC
  approvalState: 'PENDING_APPROVAL' | 'APPROVED' | 'DECLINED' | 'NO_DRIVER_AVAILABLE' | 'CANCELLED' | string;
  state?: string;               // RunState — PENDING / IN_PROGRESS / COMPLETED / ABORTED / PAUSED
  assignedDriverPublicId?: string | null;
  approvalDeadline?: string | null;
  abortReason?: string | null;
}

export async function getAvailableSlots(
  campaignId: number | string,
  opts: { limit?: number; after?: string } = {}
): Promise<SlotOption[]> {
  const { limit = 50, after } = opts;
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (after) params.set('after', after);
  const response = await apiFetch(`/campaigns/${campaignId}/available-slots?${params.toString()}`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to load available slots');
  }
  return response.json();
}

export async function bookRun(
  campaignId: number | string,
  payload: { slotStart: string; idempotencyKey: string }
): Promise<CampaignRun> {
  const response = await apiFetch(`/campaigns/${campaignId}/runs`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    // 402 = quota / no-sub block — surface structured body to caller (same pattern as activateCampaign).
    if (response.status === 402) {
      const body = await parseStructuredError(response);
      throw new ActivationError(402, body);
    }
    const errorText = await response.text();
    const error: any = new Error(errorText || 'Failed to book run');
    error.status = response.status;
    try {
      error.data = JSON.parse(errorText);
    } catch (e) {
      error.data = null;
    }
    throw error;
  }

  return response.json();
}

export async function bookRunsAuto(
  campaignId: number | string,
  payload: { count: number; idempotencyKey: string }
): Promise<{ bookedRuns: CampaignRun[]; requested: number; booked: number }> {
  const response = await apiFetch(`/campaigns/${campaignId}/runs/auto`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    if (response.status === 402) {
      const body = await parseStructuredError(response);
      throw new ActivationError(402, body);
    }
    const errorText = await response.text();
    const error: any = new Error(errorText || 'Failed to auto-book runs');
    error.status = response.status;
    try {
      error.data = JSON.parse(errorText);
    } catch (e) {
      error.data = null;
    }
    throw error;
  }

  return response.json();
}

export async function cancelRun(
  campaignId: number | string,
  runId: number | string
): Promise<void> {
  const response = await apiFetch(`/campaigns/${campaignId}/runs/${runId}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to cancel run');
  }
}

export async function getCampaignRuns(
  campaignId: number | string
): Promise<CampaignRun[]> {
  const response = await apiFetch(`/campaigns/${campaignId}/runs`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to load campaign runs');
  }
  return response.json();
}

export async function createTargetReachCampaign(payload: {
  adId: number;
  name: string;
  userPrompt: string;
  startLat: number;
  startLng: number;
  maxDrivingHoursPerDay: number;
  budgetDays: number;
  roadPreference: string;
}) {
  const response = await apiFetch('/campaigns/target-reach', {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorText = await response.text();
    const error: any = new Error(errorText || 'Failed to create target reach campaign');
    error.status = response.status;
    try {
      error.data = JSON.parse(errorText);
    } catch (e) {
      error.data = null;
    }
    throw error;
  }

  return response.json();
}

export async function getPresignedUrl(orgId, adId, name) {
  const payload = {
    "name": name,
    "orgId": orgId
  };

  return apiFetch('/ads/' + adId + '/uploadUrl', {
    method: 'POST',
    body: JSON.stringify(payload)
  }).then(parseResponse);
}

// Ad-less, org-scoped presigned upload URL — used to upload reference images
// in the create wizard BEFORE the AI ad exists. Returns { url, s3Uri }.
export async function getOrgUploadUrl(orgId: string, name: string) {
  return apiFetch('/organizations/' + orgId + '/upload-url', {
    method: 'POST',
    body: JSON.stringify({ name })
  }).then(parseResponse);
}

export async function uploadToS3(presignedUrl, file) {
  // S3 upload — not our API, no auth header
  return fetch(presignedUrl, {
    body: file,
    headers: {
      'Content-Length': file.length,
      'Content-Type': file.type
    },
    method: 'PUT'
  });
}

// --- Subscription / billing ---

async function parseStructuredError(response: Response): Promise<ActivationErrorBody> {
  try {
    const text = await response.text();
    if (!text) return { reason: 'unknown' };
    return JSON.parse(text) as ActivationErrorBody;
  } catch (e) {
    return { reason: 'unknown' };
  }
}

export async function getSubscription(): Promise<SubscriptionResponse | null> {
  const response = await apiFetch('/subscription');
  if (response.status === 204 || response.status === 404) {
    return null;
  }
  if (!response.ok) {
    return null;
  }
  return response.json();
}

export async function getTiers(): Promise<TierResponse[]> {
  const response = await apiFetch('/subscription/tiers');
  if (!response.ok) {
    return [];
  }
  return response.json();
}

export async function getTrialOffer(): Promise<TrialOfferResponse | null> {
  const response = await apiFetch('/subscription/trial-offer');
  if (response.status === 204 || response.status === 404) {
    return null;
  }
  if (!response.ok) {
    return null;
  }
  // Some backends return an empty body with 200; defend against that.
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as TrialOfferResponse;
  } catch {
    return null;
  }
}

export interface CheckoutPaymentResponse {
  clientSecret: string;
  /** Null for card-gated trials: the subscription is created by the webhook after card confirmation. */
  subscriptionId: string | null;
  /** "payment" → confirm with stripe.confirmPayment; "setup" → confirm with stripe.confirmSetup. */
  mode: 'payment' | 'setup';
}

export async function createSubscriptionCheckout(
  tier: SubscriptionTier,
  useTrial: boolean = false
): Promise<CheckoutPaymentResponse> {
  const response = await apiFetch('/subscription/checkout', {
    method: 'POST',
    body: JSON.stringify({ tier, useTrial })
  });
  if (!response.ok) {
    const body = await parseStructuredError(response);
    throw new ActivationError(response.status, body, 'Failed to start checkout');
  }
  return response.json();
}

export async function previewChange(tier: SubscriptionTier): Promise<UpgradePreviewResponse> {
  const response = await apiFetch(`/subscription/change/preview?tier=${encodeURIComponent(tier)}`);
  if (!response.ok) {
    const body = await parseStructuredError(response);
    throw new ActivationError(response.status, body, 'Failed to preview plan change');
  }
  return response.json();
}

export async function changeSubscription(
  tier: SubscriptionTier,
  prorationDate?: number | null
): Promise<void> {
  const response = await apiFetch('/subscription/change', {
    method: 'POST',
    body: JSON.stringify({ tier, prorationDate: prorationDate ?? null })
  });
  if (response.status === 409 || response.status === 402) {
    const body = await parseStructuredError(response);
    throw new ActivationError(response.status, body);
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to change subscription');
  }
}

export async function cancelSubscription(): Promise<void> {
  const response = await apiFetch('/subscription/cancel', { method: 'POST' });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to cancel subscription');
  }
}

export async function reactivateSubscription(): Promise<void> {
  const response = await apiFetch('/subscription/reactivate', { method: 'POST' });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to reactivate subscription');
  }
}

export async function openBillingPortal(): Promise<{ url: string }> {
  const response = await apiFetch('/subscription/portal', { method: 'POST' });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to open billing portal');
  }
  return response.json();
}

// ----- One-off credit top-ups -----

export async function getTopUpCatalog(): Promise<TopUpCatalogResponse> {
  const response = await apiFetch('/subscription/topups');
  if (!response.ok) {
    const body = await parseStructuredError(response);
    throw new ActivationError(response.status, body, 'Failed to load top-up packs');
  }
  return response.json();
}

export async function createTopUpCheckout(
  items: TopUpCheckoutItem[],
  idempotencyKey: string
): Promise<TopUpCheckoutResponse> {
  const response = await apiFetch('/subscription/topups/checkout', {
    method: 'POST',
    body: JSON.stringify({ items, idempotencyKey })
  });
  if (!response.ok) {
    const body = await parseStructuredError(response);
    throw new ActivationError(response.status, body, 'Failed to start top-up checkout');
  }
  return response.json();
}

// ----- Saved cards + invoices (in-app replacement for Stripe Billing Portal) -----

export async function getPaymentMethods(): Promise<PaymentMethodResponse[]> {
  const response = await apiFetch('/subscription/payment-methods');
  if (!response.ok) throw new Error('Failed to load payment methods');
  return response.json();
}

export async function createPaymentMethodSetupIntent(): Promise<SetupIntentResponse> {
  const response = await apiFetch('/subscription/payment-methods/setup-intent', {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to start adding a card');
  return response.json();
}

export async function setDefaultPaymentMethod(id: string): Promise<void> {
  const response = await apiFetch(`/subscription/payment-methods/${encodeURIComponent(id)}/default`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to set default payment method');
}

export async function removePaymentMethod(id: string): Promise<void> {
  const response = await apiFetch(`/subscription/payment-methods/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    // Surface the structured BadRequest message ("Cannot remove the default…") if present.
    const body = await parseStructuredError(response);
    throw new Error(body.message || body.reason || 'Failed to remove payment method');
  }
}

export async function getInvoices(): Promise<InvoiceResponse[]> {
  const response = await apiFetch('/subscription/invoices');
  if (!response.ok) throw new Error('Failed to load invoices');
  return response.json();
}

export const NullComponent = () => {
  return null;
};

export function generateIdempotencyKey(): string {
  return 'idem_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
}
